/**
 * Device Health summary counting contract (C-08).
 *
 * Two overlapping dimensions — not mutually exclusive partitions:
 *
 * 1. Connectivity (Online / Offline summary cards)
 *    - Online: connection label is Online
 *    - Offline: connection label is a connectivity-down status
 *      (Offline, Bridge offline, Off)
 *
 * 2. Operational attention (Needs attention note)
 *    - Any connection label with presentation tone "bad"
 *    - Includes Offline connectivity labels plus Auth error, Error, etc.
 *
 * A device that is Offline (or Bridge offline / Off) counts in BOTH Offline
 * and Needs attention. Needs attention must never remove a device from Offline.
 *
 * Bridge vs Camera: each device row is counted independently. An online Bridge
 * does not imply its Cameras are online.
 *
 * Notifications remain narrower (literal Offline only) on the backend — this
 * helper is presentation/summary counting only.
 */

export type DeviceHealthStatusTone = 'ok' | 'bad' | 'neutral';

/** Connection labels that mean the device is Online for summary counting. */
export const DEVICE_HEALTH_ONLINE_LABELS = new Set(['online']);

/**
 * Connection labels that mean connectivity is down for the Offline summary.
 * Includes EEN ``bridgeOffline`` → "Bridge offline" so cameras/bridges in that
 * state are not omitted from Offline while still appearing under Needs attention.
 */
export const DEVICE_HEALTH_OFFLINE_CONNECTIVITY_LABELS = new Set([
    'offline',
    'bridge offline',
    'off',
]);

export type DeviceHealthConnectionSource = {
    connection_status?: string | null;
    primary_status?: string | null;
};

export function normalizeDeviceHealthStatusLabel(
    status: string | null | undefined,
): string {
    return (status || '').trim().toLowerCase();
}

export function deviceHealthConnectionLabel(
    device: DeviceHealthConnectionSource,
): string {
    const raw = device.connection_status || device.primary_status;
    if (raw == null || String(raw).trim() === '') {
        return 'Unknown';
    }
    return String(raw);
}

export function isDeviceHealthOnlineLabel(status: string | null | undefined): boolean {
    return DEVICE_HEALTH_ONLINE_LABELS.has(normalizeDeviceHealthStatusLabel(status));
}

export function isDeviceHealthOfflineConnectivityLabel(
    status: string | null | undefined,
): boolean {
    return DEVICE_HEALTH_OFFLINE_CONNECTIVITY_LABELS.has(
        normalizeDeviceHealthStatusLabel(status),
    );
}

/**
 * Presentation tones for pills: healthy / attention / neutral.
 * Does not rewrite status labels.
 */
export function deviceHealthStatusTone(
    status: string | null | undefined,
): DeviceHealthStatusTone {
    const label = normalizeDeviceHealthStatusLabel(status);
    if (!label) {
        return 'neutral';
    }
    if (
        label === 'online'
        || label === 'connected'
        || label === 'operational'
    ) {
        return 'ok';
    }
    if (
        label === 'offline'
        || label === 'disconnected'
        || label === 'bridge offline'
        || label === 'auth error'
        || label === 'off'
        || label === 'error'
        || label === 'unreachable'
        || label === 'disabled'
    ) {
        return 'bad';
    }
    // Initializing, Busy, Unknown, and unknown passthrough labels stay neutral.
    return 'neutral';
}

export function isDeviceHealthAttentionLabel(
    status: string | null | undefined,
): boolean {
    return deviceHealthStatusTone(status) === 'bad';
}

export function countDeviceHealthOnline(
    devices: DeviceHealthConnectionSource[],
): number {
    return devices.filter((device) =>
        isDeviceHealthOnlineLabel(deviceHealthConnectionLabel(device)),
    ).length;
}

export function countDeviceHealthOffline(
    devices: DeviceHealthConnectionSource[],
): number {
    return devices.filter((device) =>
        isDeviceHealthOfflineConnectivityLabel(deviceHealthConnectionLabel(device)),
    ).length;
}

export function countDeviceHealthAttention(
    devices: DeviceHealthConnectionSource[],
): number {
    return devices.filter((device) =>
        isDeviceHealthAttentionLabel(deviceHealthConnectionLabel(device)),
    ).length;
}
